import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { distance, polygonToSvgPath } from '../../geometry/polygons';
import type { Line, Point } from '../../geometry/types';
import type { GameComponentProps } from '../core/types';
import {
  pointIsOutsidePolygon,
  scoreRatiole,
  segmentPassesThroughPolygon,
  type RatioleScore,
} from './logic';
import { isRatiolePuzzle, type RatiolePuzzle } from './types';

type SavedResult = { line: Line };
type RatioleSavedState = { version: 1; result: SavedResult | null };

function readSavedState(value: unknown): RatioleSavedState {
  if (!value || typeof value !== 'object') return { version: 1, result: null };
  const candidate = value as Partial<RatioleSavedState>;
  const line = candidate.result?.line;
  if (
    !line ||
    typeof line.start?.x !== 'number' ||
    typeof line.start?.y !== 'number' ||
    typeof line.end?.x !== 'number' ||
    typeof line.end?.y !== 'number'
  ) {
    return { version: 1, result: null };
  }
  return { version: 1, result: { line } };
}

function eventPoint(element: SVGSVGElement, clientX: number, clientY: number): Point {
  const bounds = element.getBoundingClientRect();
  return {
    x: Math.max(0, Math.min(1, (clientX - bounds.left) / bounds.width)),
    y: Math.max(0, Math.min(1, (clientY - bounds.top) / bounds.height)),
  };
}

function formatDelta(deltaMs: number) {
  const seconds = deltaMs / 1_000;
  return `${seconds > 0 ? '+' : seconds < 0 ? '−' : ''}${Math.abs(seconds).toFixed(1)}s`;
}

export function RatioleGame({
  puzzle,
  savedState,
  disabled,
  reducedMotion,
  onStateChange,
  onComplete,
}: GameComponentProps) {
  if (!isRatiolePuzzle(puzzle.payload)) throw new Error('Ratiole received an invalid puzzle.');
  const ratiole = puzzle.payload as RatiolePuzzle;
  const initial = useMemo(() => readSavedState(savedState), [savedState]);
  const initialScore = initial.result
    ? scoreRatiole(ratiole.polygon, ratiole.targetPercent, initial.result.line)
    : null;
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [previewPoint, setPreviewPoint] = useState<Point | null>(null);
  const [keyboardCursor, setKeyboardCursor] = useState<Point>({ x: 0.06, y: 0.5 });
  const [resultLine, setResultLine] = useState<Line | null>(initial.result?.line ?? null);
  const [score, setScore] = useState<RatioleScore | null>(initialScore);
  const [status, setStatus] = useState(
    initialScore
      ? `You cut ${initialScore.achievedPercent.toFixed(1)} : ${(100 - initialScore.achievedPercent).toFixed(1)}`
      : 'Start outside the filled shape',
  );
  const pointerRef = useRef<number | null>(null);
  const completionSentRef = useRef(false);

  useEffect(() => {
    if (!score || completionSentRef.current) return;
    const handle = window.setTimeout(
      () => {
        completionSentRef.current = true;
        onComplete({
          kind: score.error <= 7 ? 'solved' : 'failed',
          baseDeltaMs: score.deltaMs,
          message: `You cut ${score.achievedPercent.toFixed(1)} : ${(100 - score.achievedPercent).toFixed(1)}`,
          metadata: {
            achievedPercent: score.achievedPercent,
            targetPercent: ratiole.targetPercent,
            error: score.error,
          },
        });
      },
      reducedMotion ? 180 : 1_000,
    );
    return () => window.clearTimeout(handle);
  }, [onComplete, ratiole.targetPercent, reducedMotion, score]);

  const begin = (point: Point) => {
    if (disabled || score) return false;
    if (!pointIsOutsidePolygon(point, ratiole.polygon)) {
      setStatus('Begin outside the filled shape');
      return false;
    }
    setStartPoint(point);
    setPreviewPoint(point);
    setStatus('Drag a straight line through the shape and release outside');
    return true;
  };

  const submit = (end: Point) => {
    if (!startPoint || score) return;
    if (distance(startPoint, end) < 0.08) {
      setStartPoint(null);
      setPreviewPoint(null);
      setStatus('Cut was too short · try again');
      return;
    }
    if (!pointIsOutsidePolygon(end, ratiole.polygon)) {
      setStartPoint(null);
      setPreviewPoint(null);
      setStatus('Finish outside the filled shape · try again');
      return;
    }
    const line = { start: startPoint, end };
    if (!segmentPassesThroughPolygon(line, ratiole.polygon)) {
      setStartPoint(null);
      setPreviewPoint(null);
      setStatus('Drag the segment all the way through the shape · try again');
      return;
    }
    const nextScore = scoreRatiole(ratiole.polygon, ratiole.targetPercent, line);
    if (!nextScore) {
      setStartPoint(null);
      setPreviewPoint(null);
      setStatus('That line did not split the shape · try again');
      return;
    }
    setResultLine(line);
    setScore(nextScore);
    setStartPoint(null);
    setPreviewPoint(null);
    setStatus(
      `You cut ${nextScore.achievedPercent.toFixed(1)} : ${(100 - nextScore.achievedPercent).toFixed(1)} · ${formatDelta(nextScore.deltaMs)}`,
    );
    onStateChange({ version: 1, result: { line } });
  };

  const pointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (
      (event.button ?? 0) !== 0 ||
      !begin(eventPoint(event.currentTarget, event.clientX, event.clientY))
    )
      return;
    pointerRef.current = event.pointerId;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const pointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (pointerRef.current !== event.pointerId || !startPoint) return;
    setPreviewPoint(eventPoint(event.currentTarget, event.clientX, event.clientY));
  };

  const pointerUp = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (pointerRef.current !== event.pointerId || !startPoint) return;
    const end = eventPoint(event.currentTarget, event.clientX, event.clientY);
    pointerRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    submit(end);
  };

  const cancel = () => {
    pointerRef.current = null;
    setStartPoint(null);
    setPreviewPoint(null);
    setStatus('Cut cancelled · try again');
  };

  const keyboard = (event: ReactKeyboardEvent<SVGSVGElement>) => {
    if (disabled || score) return;
    const movement: Record<string, Point> = {
      ArrowLeft: { x: -0.025, y: 0 },
      ArrowRight: { x: 0.025, y: 0 },
      ArrowUp: { x: 0, y: -0.025 },
      ArrowDown: { x: 0, y: 0.025 },
    };
    if (movement[event.key]) {
      event.preventDefault();
      const next = {
        x: Math.max(0, Math.min(1, keyboardCursor.x + movement[event.key].x)),
        y: Math.max(0, Math.min(1, keyboardCursor.y + movement[event.key].y)),
      };
      setKeyboardCursor(next);
      if (startPoint) setPreviewPoint(next);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (startPoint) submit(keyboardCursor);
      else begin(keyboardCursor);
    } else if (event.key === 'Escape') cancel();
  };

  const visibleLine =
    resultLine ?? (startPoint && previewPoint ? { start: startPoint, end: previewPoint } : null);

  return (
    <section className="ratiole-game" aria-labelledby="ratiole-title">
      <div className="ratiole-target">
        <span>Target ratio</span>
        <h1 id="ratiole-title">
          {ratiole.targetPercent} <b>:</b> {100 - ratiole.targetPercent}
        </h1>
      </div>
      <svg
        className="geometry-canvas ratiole-canvas"
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        tabIndex={disabled || score ? -1 : 0}
        role="application"
        aria-label={`Cut the shape to ${ratiole.targetPercent} to ${100 - ratiole.targetPercent}. Press Space to start or submit with keyboard arrows.`}
        aria-disabled={disabled || Boolean(score)}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={cancel}
        onKeyDown={keyboard}
      >
        <rect width="1" height="1" className="ratiole-background" />
        {score ? (
          <>
            <path
              d={polygonToSvgPath(score.cut.first)}
              className="ratiole-piece ratiole-piece--first"
              fillRule="evenodd"
            />
            <path
              d={polygonToSvgPath(score.cut.second)}
              className="ratiole-piece ratiole-piece--second"
              fillRule="evenodd"
            />
          </>
        ) : (
          <path
            d={polygonToSvgPath(ratiole.polygon)}
            className="ratiole-shape"
            fillRule="evenodd"
          />
        )}
        {visibleLine && (
          <line
            x1={visibleLine.start.x}
            y1={visibleLine.start.y}
            x2={visibleLine.end.x}
            y2={visibleLine.end.y}
            className={`ratiole-cut-line ${score ? 'ratiole-cut-line--result' : 'ratiole-cut-line--preview'}`}
          />
        )}
      </svg>
      <p className="geometry-instruction">Start outside · drag through · release outside</p>
      <p className="geometry-status" role="status" aria-live="assertive">
        {status}
      </p>
    </section>
  );
}
